import {Injectable} from '@angular/core';
import * as moment from 'moment';

/**
 * Wrapper around `sessionStorage` used to cache infinite-table filter state.
 *
 * Backed by `sessionStorage` so that closing the tab or browser drops every
 * cached filter automatically. Logout via the top-bar Logout link uses a
 * same-tab `window.location.href` navigation, which `sessionStorage` survives,
 * so the logout flow still calls `clearFilterStorage()` explicitly to flush
 * anything lingering before the next user signs in on the same tab.
 *
 * The 24-hour TTL is kept as a belt-and-braces safety net for long-running
 * single-tab sessions; in practice the tab close usually clears state first.
 *
 * Cross-tab session-timeout coordination (the `sharedUserActivity` heartbeat
 * in SessionExpirationService) is deliberately NOT routed through this
 * service — that signal needs real `localStorage` so the `storage` event
 * fires across tabs, which `sessionStorage` does not support.
 */
@Injectable()
export class SessionStorageService {

  private readonly TIME: moment.DurationInputArg1 = 24;
  private readonly UNITS: moment.unitOfTime.DurationConstructor = 'h';

  constructor() {
  }

  public setItem(key: string, data: any): void {
    sessionStorage.setItem(key, JSON.stringify(data));
    // Store a timestamp for this specific key to enable per-key expiration
    sessionStorage.setItem(key + '_TIMESTAMP', moment().toISOString());
    // Also maintain global SET_DATE for backward compatibility
    sessionStorage.setItem('SET_DATE', moment().toISOString());
  }

  public getItem(key: string): any {
    // Check if this specific key has expired before retrieving
    if (this.isKeyExpired(key)) {
      this.removeItem(key);
      this.removeItem(key + '_TIMESTAMP');
      return null;
    }

    const item = sessionStorage.getItem(key);
    if (!item) {
      return null;
    }
    try {
      return JSON.parse(item);
    } catch (e) {
      // If parsing fails, return null
      return null;
    }
  }

  private isKeyExpired(key: string): boolean {
    const timestampKey = key + '_TIMESTAMP';
    const timestamp = sessionStorage.getItem(timestampKey);
    if (!timestamp) {
      // If no timestamp exists, check the global SET_DATE for backward compatibility
      const globalDate = sessionStorage.getItem('SET_DATE');
      if (globalDate) {
        return moment(globalDate).isBefore(moment().subtract(this.TIME, this.UNITS));
      }
      return false; // No timestamp means it's not expired (might be old data without timestamp)
    }
    return moment(timestamp).isBefore(moment().subtract(this.TIME, this.UNITS));
  }

  public checkValidity(keys: Array<string>, fullClear?: boolean): boolean {
    if (this.hasItem('SET_DATE')) {
      if (moment(sessionStorage.getItem('SET_DATE')).isBefore(moment().subtract(this.TIME, this.UNITS))) {
        if (keys && keys.length > 0) {
          for (const key of keys) {
            this.removeItem(key);
          }
          this.setItem('SET_DATE', moment().toISOString());
          return true;
        } else {
          if (fullClear) {
            this.clear();
            this.setItem('SET_DATE', moment().toISOString());
            return true;
          }
        }
      }
    }

    return false;
  }

  public hasItem(key: string): Boolean {
    return !!sessionStorage.getItem(key);
  }

  public removeItem(key: string): void {
    sessionStorage.removeItem(key);
    sessionStorage.removeItem(key + '_TIMESTAMP');
  }

  public clear() {
    sessionStorage.clear();
  }

  /**
   * Remove every infinite-table filter cache entry. All filter storage keys follow
   * the `*_TABLE_*` naming convention (see InfiniteTableComponent's
   * tableOptions.filterStorageKey). Called on logout so the same tab doesn't
   * carry filters into the next user's session (sessionStorage survives the
   * same-tab navigation to j_spring_security_logout).
   */
  public clearFilterStorage(): void {
    const toRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.indexOf('_TABLE_') !== -1) {
        toRemove.push(key);
      }
    }
    toRemove.forEach(key => this.removeItem(key));
  }
}
